"use client";
import React, { useState, useRef, useEffect, useMemo } from "react";
import Fuse from "fuse.js";
import { ChevronsUpDown } from "lucide-react";
import { FilterDropdownProps } from "./types";
import { Checkbox } from "@/components/atoms/Checkbox";

export function FilterDropdown({ label, values, options, onChange }: FilterDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleToggleDropdown = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({
                top: rect.top,
                left: rect.left,
                width: rect.width,
            });
            setSearch("");
            setDebouncedSearch("");
        }
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => searchRef.current?.focus(), 0);
        }
    }, [isOpen]);

    const [cleared, setCleared] = useState(false);
    const isAllSelected = !(cleared && values.length === 0) && (values.length === 0 || values.length === options.length);

    const handleToggle = (option: string) => {
        if (option === "All") {
            setCleared(isAllSelected);
            onChange([]);
            return;
        }
        const next = isAllSelected
            ? options.filter((o) => o !== option)
            : values.includes(option)
              ? values.filter((v) => v !== option)
              : [...values, option];
        setCleared(next.length === 0);
        onChange(next);
    };

    const fuse = useMemo(
        () =>
            new Fuse(options, {
                threshold: 0.3,
                minMatchCharLength: 2,
                ignoreLocation: true,
            }),
        [options],
    );

    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 100);
        return () => clearTimeout(timer);
    }, [search]);

    const filteredOptions = debouncedSearch
        ? fuse.search(debouncedSearch).map((r) => r.item)
        : options;

    const displayText = isAllSelected ? "All" : values.length === 1 ? values[0] : `${values.length} Selected`;

    return (
        <div ref={dropdownRef} className="flex items-center gap-1">
            <div
                ref={buttonRef}
                onClick={handleToggleDropdown}
                className="flex items-center gap-1 cursor-pointer bg-[#f5f5f5] rounded-full px-3 py-1 hover:bg-[#ebebeb] transition-colors"
            >
                <span className="font-roboto font-normal text-menu-hover text-sm whitespace-nowrap">{label}:</span>
                <span className="font-roboto font-normal text-title-color text-sm whitespace-nowrap">{displayText}</span>
                <ChevronsUpDown size={14} className="text-[#5A717A]" />
            </div>

            {isOpen && (
                <div
                    className="fixed font-roboto bg-white border border-gray-200 rounded shadow-lg min-w-[150px] z-[9999] flex flex-col"
                    style={{
                        top: `${position.top + 30}px`,
                        left: `${position.left}px`,
                        minWidth: `${position.width + 50}px`,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="px-2 py-2 border-b border-gray-100">
                        <input
                            ref={searchRef}
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search..."
                            className="w-full text-sm px-2 py-1 border border-gray-200 rounded outline-none focus:border-[#1f8894]"
                        />
                    </div>
                    <div
                        onClick={(e) => { e.stopPropagation(); handleToggle("All"); }}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-50 text-sm text-title-color flex items-center gap-2 border-b border-gray-100"
                    >
                        <Checkbox checked={isAllSelected} readOnly />
                        <span>All</span>
                    </div>
                    <div className="max-h-[220px] overflow-y-auto">
                        {filteredOptions.length === 0 && (
                            <p className="px-3 py-2 text-sm text-gray-400">No results</p>
                        )}
                        {filteredOptions.map((option) => {
                            const isChecked = isAllSelected || values.includes(option);
                            return (
                                <div
                                    key={option}
                                    onClick={(e) => { e.stopPropagation(); handleToggle(option); }}
                                    className="px-3 py-2 cursor-pointer hover:bg-gray-50 text-sm text-title-color flex items-center gap-2"
                                >
                                    <Checkbox checked={isChecked} readOnly />
                                    <span>{option}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
